import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Castle, PartyPopper, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if already authenticated
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          // Check if user has an organization
          if (data.organization) {
            navigate("/dashboard", { replace: true });
          } else {
            navigate("/onboarding", { replace: true });
          }
          return;
        }
      } catch (error) {
        // Expected for unauthenticated users - show login page
        if (error?.message !== "Failed to fetch") {
          console.warn("Auth check:", error?.message);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen festive-gradient flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-festive-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen festive-gradient confetti-pattern flex items-center justify-center p-4">
      <Card className="w-full max-w-md card-festive animate-fade-in">
        <CardContent className="p-8">
          {/* Logo Section */}
          <div className="text-center mb-8">
            <div className="relative inline-block mb-4">
              <div className="w-20 h-20 bg-gradient-to-br from-festive-pink to-festive-purple rounded-3xl flex items-center justify-center shadow-lg">
                <Castle className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-festive-yellow rounded-full flex items-center justify-center shadow-md animate-bounce-soft">
                <Sparkles className="w-3 h-3 text-yellow-700" />
              </div>
            </div>
            <h1 className="text-3xl font-heading font-bold text-gradient mb-2">
              Espace Admin ResaFlow.ai™
            </h1>
            <p className="text-muted-foreground font-body">
              Gestion des réservations
            </p>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-8">
            <FeatureItem 
              icon={<Castle className="w-5 h-5" />}
              text="Gérez vos structures gonflables"
            />
            <FeatureItem 
              icon={<PartyPopper className="w-5 h-5" />}
              text="Organisez les packs festifs"
            />
            <FeatureItem 
              icon={<Sparkles className="w-5 h-5" />}
              text="Suivez vos réservations"
            />
          </div>

          {/* Login Button */}
          <Button
            data-testid="login-google-btn"
            onClick={handleLogin}
            className="w-full h-14 bg-gradient-to-r from-festive-pink to-festive-purple hover:opacity-90 text-white font-semibold text-lg rounded-2xl shadow-button transition-transform hover:scale-[1.02] active:scale-[0.98]"
          >
            <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Connexion avec Google
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Accès réservé aux administrateurs
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

const FeatureItem = ({ icon, text }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl bg-pink-50/50 border border-pink-100">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-festive-pink/20 to-festive-purple/20 flex items-center justify-center text-festive-pink">
      {icon}
    </div>
    <span className="text-sm font-medium text-gray-700">{text}</span>
  </div>
);
